import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { logo } from '../../constants/icons';

// TP-Coach-Mandate — public invitee chain, mirrored 1:1 from
// PlayerMandateSelection (Brief §4.5). Subject is a coach/staff member; the
// identity key is the TheSports coach id (ts_coach_id) instead of player_id.
const StaffMandateSelection = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const location = useLocation();

  const searchParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  // Coming back from the verify step the token survives in the path, but the
  // query is gone — the invite then lives only in location.state. Query keeps
  // precedence, state is the fallback.
  const stateInvite = location.state || {};

  const queryToken = searchParams.get('invite_token');
  const tsCoachId =
    searchParams.get('ts_coach_id') ||
    searchParams.get('coach_id') ||
    stateInvite.tsCoachId ||
    null;
  const inviteEmail = searchParams.get('email') || stateInvite.email || null;
  const inviteType = searchParams.get('type') || stateInvite.type || null;
  const inviteSentBy =
    searchParams.get('invite_sent_by') || stateInvite.inviteSentBy || null;
  const senderType =
    searchParams.get('sender_type') || stateInvite.senderType || null;
  const agencyId = searchParams.get('agency_id') || stateInvite.agencyId || null;
  const inviteMobile =
    searchParams.get('mobile')?.trim() || stateInvite.mobile || null;
  const mandateToken = token || queryToken || stateInvite.token || null;

  const inviteParams = React.useMemo(() => {
    const paramsObject = { ...(stateInvite.inviteParams || {}) };
    searchParams.forEach((value, key) => {
      paramsObject[key] = value;
    });
    return paramsObject;
  }, [searchParams, stateInvite.inviteParams]);

  const sharedState = {
    token: mandateToken,
    tsCoachId: tsCoachId || null,
    email: inviteEmail,
    type: inviteType,
    inviteSentBy,
    senderType,
    agencyId,
    mobile: inviteMobile,
    rawQuery: location.search || stateInvite.rawQuery || '',
    inviteParams,
  };

  const handleContinueWithSignup = () => {
    navigate(`/register${sharedState.rawQuery}`, {
      state: {
        mandateInvite: sharedState,
      },
    });
  };

  const handleContinueWithoutSignup = () => {
    const basePath = mandateToken
      ? `/staff-register-mandate/${mandateToken}`
      : '/staff-register-mandate';

    navigate(`${basePath}/verify`, {
      state: sharedState,
    });
  };

  return (
    <div className="flex min-h-screen">
      <div className="flex-1 bg-white flex items-center justify-center">
        <div className="w-full max-w-xl p-6 border border-gray-400 rounded-xl">
          <div className="text-center mb-8">
            <img src={logo} alt="Logo" className="mx-auto mb-4 h-10" />

            <h2 className="text-[24px] leading-[32px] font-semibold text-gray-900 mb-3">
              Staff Representation Invitation
            </h2>
            <p className="text-[16px] leading-[24px] text-gray-600 mb-8">
              You've been invited to join as a coach or staff member. Choose how you'd like to proceed.
            </p>
          </div>

          <div className="space-y-4">
            {/* Continue with Signup */}
            <button
              onClick={handleContinueWithSignup}
              className="w-full py-4 px-6 bg-[#2283FF] hover:bg-[#1a6cd4] text-white rounded-lg transition-colors font-medium text-[16px] flex items-center justify-center gap-3"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20 8V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M23 11H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Continue with Signup
            </button>

            {/* Continue without Signup */}
            <button
              onClick={handleContinueWithoutSignup}
              className="w-full py-4 px-6 border-2 border-[#E9E9E9] hover:border-[#2283FF] text-[#1A212F] rounded-lg transition-colors font-medium text-[16px] flex items-center justify-center gap-3"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Continue without Signup
            </button>

            <p className="text-center text-sm text-gray-500 mt-6">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffMandateSelection;
